import React, { useState } from 'react';
import type { ReactNode } from 'react';

export interface TabItem {
  id: string;
  label: string;
  content: ReactNode;
  badge?: number;
  icon?: ReactNode;
}

interface TabContainerProps {
  tabs: TabItem[];
  defaultActiveTab?: string;
  className?: string;
  onTabChange?: (tabId: string) => void;
}

export const TabContainer: React.FC<TabContainerProps> = ({
  tabs,
  defaultActiveTab,
  className = '',
  onTabChange,
}) => {
  const [activeTab, setActiveTab] = useState(defaultActiveTab || tabs[0]?.id);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    onTabChange?.(tabId);
  };

  const activeTabContent = tabs.find(t => t.id === activeTab)?.content;

  return (
    <div className={className} style={{ width: '100%' }}>
      {/* Tab bar */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #1e2d45',
        background: '#0f1621',
        padding: '0 1rem',
        gap: '0.125rem',
        overflowX: 'auto',
      }}>
        {tabs.map(tab => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              aria-current={active ? 'page' : undefined}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                padding: '0.75rem 0.875rem',
                border: 'none', borderBottom: `2px solid ${active ? '#3b82f6' : 'transparent'}`,
                background: 'transparent', cursor: 'pointer',
                fontSize: '0.8125rem', fontWeight: active ? 700 : 500,
                color: active ? '#60a5fa' : '#475569',
                transition: 'color 0.18s, border-color 0.18s',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                letterSpacing: '-0.005em',
              }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.color = '#94a3b8'; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.color = '#475569'; }}
            >
              {tab.icon && (
                <span style={{ opacity: active ? 1 : 0.6, display: 'flex', alignItems: 'center' }}>
                  {tab.icon}
                </span>
              )}
              {tab.label}
              {tab.badge !== undefined && tab.badge > 0 && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 18, height: 18, padding: '0 5px',
                  borderRadius: 99,
                  fontSize: '0.65rem', fontWeight: 700,
                  background: active ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.06)',
                  color: active ? '#60a5fa' : '#475569',
                }}>
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div style={{ background: '#111827' }}>
        {activeTabContent}
      </div>
    </div>
  );
};
